import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Divider,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  AlertBannerContext,
  BRAND_COLOR,
  BRAND_HOVER,
  PhoneCountryCodeMap,
  COUNTRY_CODE,
  validatePhoneWithCountrySelection,
} from "@kivunova/kivufrontendcommon";
import { ROUTE_PLANS, ROUTE_SUCCESS } from "../../constants/routes";
import AnimatedContainer from "../common/AnimatedContainer";
import { PaymentMethod } from "../Payments/enums/paymentEnums";
import { useCreateSubscription } from "../../hooks/api/useSubscription";

interface PlanData {
  planId: string;
  name: string;
  price: number;
  currency: string;
  billingCycle: string;
  billingCycleId?: string;
  durationValue?: number;
  durationUnit?: "DAY" | "MONTH";
  description?: string;
}

// Card validation utilities
const luhnCheck = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s+/g, "");
  if (!/^\d+$/.test(cleaned)) return false;

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

const validateCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s+/g, "");
  return cleaned.length >= 13 && cleaned.length <= 19 && luhnCheck(cardNumber);
};

const validateExpiry = (expiry: string): boolean => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;

  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;

  if (month < 1 || month > 12) return false;
  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;

  return true;
};

const validateCVC = (cvc: string): boolean => {
  return /^\d{3,4}$/.test(cvc);
};

const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\s+/g, "");
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(" ") : cleaned;
};

const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length >= 2) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
  }
  return cleaned;
};

const OrderSummary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(
    PaymentMethod.MTN_MOMO,
  );
  const [mtnPhoneCountryCode, setMtnPhoneCountryCode] = useState<COUNTRY_CODE>(
    COUNTRY_CODE.RWANDA,
  );
  const [mtnPhoneNumber, setMtnPhoneNumber] = useState("");
  const [mtnPhoneError, setMtnPhoneError] = useState("");
  const [airtelPhoneCountryCode, setAirtelPhoneCountryCode] =
    useState<COUNTRY_CODE>(COUNTRY_CODE.RWANDA);
  const [airtelPhoneNumber, setAirtelPhoneNumber] = useState("");
  const [airtelPhoneError, setAirtelPhoneError] = useState("");
  const [cardEmail, setCardEmail] = useState("");
  const [cardEmailError, setCardEmailError] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardExpiryError, setCardExpiryError] = useState("");
  const [cardCVC, setCardCVC] = useState("");
  const [cardCVCError, setCardCVCError] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardNameError, setCardNameError] = useState("");
  const [cardCountry, setCardCountry] = useState("RW");
  const [subscribeError, setSubscribeError] = useState("");

  // Get plan data from navigation state or default to empty
  const planData = (location.state as { plan?: PlanData })?.plan || null;
  const { setAlert } = useContext(AlertBannerContext);
  const createSubscriptionMutation = useCreateSubscription();

  const handleBack = () => {
    navigate(ROUTE_PLANS);
  };

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedMethod(event.target.value as PaymentMethod);
    setSubscribeError("");
    // Clear errors when switching methods
    setMtnPhoneError("");
    setAirtelPhoneError("");
    setCardEmailError("");
    setCardNumberError("");
    setCardExpiryError("");
    setCardCVCError("");
    setCardNameError("");
  };

  // Phone validation handlers
  const validateMtnPhone = (phone?: string, countryCode?: COUNTRY_CODE) => {
    const phoneToValidate = phone !== undefined ? phone : mtnPhoneNumber;
    const countryToValidate =
      countryCode !== undefined ? countryCode : mtnPhoneCountryCode;

    if (!phoneToValidate) {
      setMtnPhoneError("Phone number is required");
      return false;
    }
    const isValid = validatePhoneWithCountrySelection(
      phoneToValidate,
      countryToValidate,
    );
    if (!isValid) {
      setMtnPhoneError("Invalid phone number for selected country");
      return false;
    }
    setMtnPhoneError("");
    return true;
  };

  const validateAirtelPhone = (phone?: string, countryCode?: COUNTRY_CODE) => {
    const phoneToValidate = phone !== undefined ? phone : airtelPhoneNumber;
    const countryToValidate =
      countryCode !== undefined ? countryCode : airtelPhoneCountryCode;

    if (!phoneToValidate) {
      setAirtelPhoneError("Phone number is required");
      return false;
    }
    const isValid = validatePhoneWithCountrySelection(
      phoneToValidate,
      countryToValidate,
    );
    if (!isValid) {
      setAirtelPhoneError("Invalid phone number for selected country");
      return false;
    }
    setAirtelPhoneError("");
    return true;
  };

  // Card validation handlers
  const validateCardForm = (): boolean => {
    let isValid = true;

    // Email validation
    if (!cardEmail) {
      setCardEmailError("Email is required");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardEmail)) {
      setCardEmailError("Invalid email format");
      isValid = false;
    } else {
      setCardEmailError("");
    }

    // Card number validation
    if (!cardNumber) {
      setCardNumberError("Card number is required");
      isValid = false;
    } else if (!validateCardNumber(cardNumber)) {
      setCardNumberError("Invalid card number");
      isValid = false;
    } else {
      setCardNumberError("");
    }

    // Expiry validation
    if (!cardExpiry) {
      setCardExpiryError("Expiry date is required");
      isValid = false;
    } else if (!validateExpiry(cardExpiry)) {
      setCardExpiryError("Invalid or expired date (MM/YY)");
      isValid = false;
    } else {
      setCardExpiryError("");
    }

    // CVC validation
    if (!cardCVC) {
      setCardCVCError("CVC is required");
      isValid = false;
    } else if (!validateCVC(cardCVC)) {
      setCardCVCError("Invalid CVC (3-4 digits)");
      isValid = false;
    } else {
      setCardCVCError("");
    }

    // Name validation
    if (!cardName) {
      setCardNameError("Name on card is required");
      isValid = false;
    } else if (cardName.trim().length < 2) {
      setCardNameError("Name must be at least 2 characters");
      isValid = false;
    } else {
      setCardNameError("");
    }

    return isValid;
  };

  const handleSubscribe = async () => {
    setSubscribeError("");
    if (!planData?.billingCycleId) {
      const msg = "Billing cycle is missing. Please go back and select a plan.";
      setSubscribeError(msg);
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.VALIDATION_ERROR,
      });
      return;
    }

    // Validate based on selected payment method
    let isValid = true;
    if (selectedMethod === PaymentMethod.MTN_MOMO) {
      isValid = validateMtnPhone();
      if (!isValid) {
        const msg = "Please enter a valid MTN phone number.";
        setSubscribeError(msg);
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.VALIDATION_ERROR,
        });
        return;
      }
    } else if (selectedMethod === PaymentMethod.AIRTEL_MONEY) {
      isValid = validateAirtelPhone();
      if (!isValid) {
        const msg = "Please enter a valid Airtel phone number.";
        setSubscribeError(msg);
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.VALIDATION_ERROR,
        });
        return;
      }
    } else if (selectedMethod === PaymentMethod.CREDIT_DEBIT) {
      isValid = validateCardForm();
      if (!isValid) {
        const msg = "Please correct the card details and try again.";
        setSubscribeError(msg);
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.VALIDATION_ERROR,
        });
        return;
      }
    }

    try {
      await createSubscriptionMutation.mutateAsync({
        planId: planData.planId,
        billingCycleId: planData.billingCycleId,
      });
      navigate(ROUTE_SUCCESS, {
        state: { plan: planData },
      });
    } catch {
      // Error shown via fetchGraphQL / setAlert
    }
  };

  if (!planData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
          position: "relative",
          overflow: "hidden",
          py: 6,
          px: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                lineHeight: 1.3,
                mb: 2,
              }}
            >
              No plan selected
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                color: "text.secondary",
                mb: 3,
              }}
            >
              Please select a plan to continue.
            </Typography>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER },
              }}
            >
              Browse Plans
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const totalAmount = planData.price;
  const monthlyAmount = planData.price;

  const paymentMethods = [
    {
      value: PaymentMethod.MTN_MOMO,
      label: "MTN Momo",
      icon: <AccountBalanceWalletIcon />,
      description: "Pay using MTN Mobile Money",
    },
    {
      value: PaymentMethod.AIRTEL_MONEY,
      label: "Airtel Money",
      icon: <AccountBalanceWalletIcon />,
      description: "Pay using Airtel Money",
    },
    {
      value: PaymentMethod.CREDIT_DEBIT,
      label: "Pay With Card",
      icon: <CreditCardIcon />,
      description: "Pay with Visa, Mastercard, or other cards",
    },
    {
      value: PaymentMethod.PAYPAL,
      label: "PayPal",
      icon: <PaymentIcon />,
      description: "Pay securely with your PayPal account",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)",
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)",
            },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {/* Page Title */}
          <Typography
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "1.25rem",
                sm: "1.60rem",
                md: "2.65rem",
                lg: "2.75rem",
              },
              lineHeight: 1.3,
              mb: 2,
              color: "white",
              textAlign: "center",
              letterSpacing: { xs: "0.01em", md: "0.02em" },
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Review and Complete Subscription
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
              lineHeight: 1.7,
              mb: 2,
              color: "rgba(255, 255, 255, 0.9)",
              textAlign: "center",
              maxWidth: "700px",
              mx: "auto",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
              fontWeight: 400,
            }}
          >
            Please review your subscription details on the left and select your
            preferred payment method on the right. Complete the payment
            information to activate your subscription.
          </Typography>

          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                minHeight: "600px",
              }}
            >
              {/* Left Section - Order Summary */}
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "0 1 40%" },
                  p: 3,
                  borderRight: { md: "1px solid rgba(0,0,0,0.1)" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Button
                    onClick={handleBack}
                    sx={{
                      minWidth: "auto",
                      p: 1,
                      mr: 2,
                      color: "text.secondary",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <ArrowBackIcon />
                  </Button>
                  <Typography
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                      lineHeight: 1.4,
                    }}
                  >
                    Pay Membership
                  </Typography>
                </Box>

                {/* Total Amount - smaller than "Pay Membership" title for correct hierarchy */}
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                    lineHeight: 1.3,
                    mb: 1,
                    color: "text.primary",
                  }}
                >
                  {planData.currency} {totalAmount.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "0.9rem", md: "1rem" },
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  Then {planData.currency} {monthlyAmount.toLocaleString()}{" "}
                  billed every {planData.billingCycle}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Coupon Input */}
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: BRAND_COLOR,
                    color: BRAND_COLOR,
                    py: 1.5,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: BRAND_HOVER,
                      backgroundColor: "rgba(111, 73, 201, 0.04)",
                    },
                  }}
                >
                  I have a coupon!
                </Button>

                {/* Footer */}
                <Box
                  sx={{
                    mt: "auto",
                    pt: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Secure payment
                  </Typography>
                  <Typography
                    component="a"
                    href="#"
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Terms
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    and
                  </Typography>
                  <Typography
                    component="a"
                    href="#"
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Privacy
                  </Typography>
                </Box>
              </Box>

              {/* Right Section - Payment Options */}
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "0 1 60%" },
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                    lineHeight: 1.3,
                    mb: 2,
                  }}
                >
                  Select Payment Method
                </Typography>

                {/* Payment Methods */}
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={selectedMethod}
                    onChange={handlePaymentMethodChange}
                  >
                    {paymentMethods.map((method) => {
                      const isSelected = selectedMethod === method.value;
                      return (
                        <Paper
                          key={method.value}
                          elevation={isSelected ? 4 : 1}
                          sx={{
                            mb: 1.5,
                            borderRadius: 2,
                            border: isSelected
                              ? `2px solid ${BRAND_COLOR}`
                              : "2px solid transparent",
                            transition: "all 0.3s ease",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "rgba(111, 73, 201, 0.02)",
                              },
                            }}
                            onClick={() =>
                              setSelectedMethod(method.value as PaymentMethod)
                            }
                          >
                            <FormControlLabel
                              value={method.value}
                              control={<Radio />}
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      color: BRAND_COLOR,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {method.icon}
                                  </Box>
                                  <Box>
                                    <Typography
                                      fontWeight="bold"
                                      sx={{
                                        fontSize: {
                                          xs: "1rem",
                                          sm: "1.125rem",
                                          md: "1.25rem",
                                        },
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {method.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {method.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              sx={{ m: 0, width: "100%" }}
                            />
                          </Box>

                          {/* Collapsible Content */}
                          <Collapse in={isSelected}>
                            <Box sx={{ px: 3, pb: 2, pt: 0 }}>
                              <Divider sx={{ mb: 2 }} />

                              {/* MTN Momo Content */}
                              {method.value === PaymentMethod.MTN_MOMO && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ mb: 2, fontWeight: 500 }}
                                  >
                                    Enter your MTN Mobile Money phone number
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <FormControl
                                      sx={{
                                        flex: "0 0 160px",
                                        minWidth: 140,
                                      }}
                                      variant="outlined"
                                    >
                                      <InputLabel id="mtn-phone-country-code-label">
                                        Code
                                      </InputLabel>
                                      <Select
                                        value={mtnPhoneCountryCode}
                                        onChange={(e) => {
                                          const newCountryCode = e.target
                                            .value as COUNTRY_CODE;
                                          setMtnPhoneCountryCode(
                                            newCountryCode,
                                          );
                                          if (mtnPhoneNumber) {
                                            validateMtnPhone(
                                              mtnPhoneNumber,
                                              newCountryCode,
                                            );
                                          }
                                        }}
                                        labelId="mtn-phone-country-code-label"
                                        id="mtn-phone-country-code"
                                        label="Code"
                                      >
                                        {Object.keys(PhoneCountryCodeMap).map(
                                          (key) => {
                                            const countryCode =
                                              key as COUNTRY_CODE;
                                            return (
                                              <MenuItem
                                                key={key}
                                                value={countryCode}
                                              >
                                                {`${PhoneCountryCodeMap[countryCode].flag} ${PhoneCountryCodeMap[countryCode].code}`}
                                              </MenuItem>
                                            );
                                          },
                                        )}
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      label="Phone Number"
                                      value={mtnPhoneNumber}
                                      onChange={(e) => {
                                        setMtnPhoneNumber(e.target.value);
                                        if (mtnPhoneError) {
                                          validateMtnPhone();
                                        }
                                      }}
                                      onBlur={validateMtnPhone}
                                      fullWidth
                                      placeholder="782484464"
                                      error={!!mtnPhoneError}
                                      helperText={mtnPhoneError}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Airtel Money Content */}
                              {method.value === PaymentMethod.AIRTEL_MONEY && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ mb: 2, fontWeight: 500 }}
                                  >
                                    Enter your Airtel Money phone number
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <FormControl
                                      sx={{
                                        flex: "0 0 160px",
                                        minWidth: 140,
                                      }}
                                      variant="outlined"
                                    >
                                      <InputLabel id="airtel-phone-country-code-label">
                                        Code
                                      </InputLabel>
                                      <Select
                                        value={airtelPhoneCountryCode}
                                        onChange={(e) => {
                                          const newCountryCode = e.target
                                            .value as COUNTRY_CODE;
                                          setAirtelPhoneCountryCode(
                                            newCountryCode,
                                          );
                                          if (airtelPhoneNumber) {
                                            validateAirtelPhone(
                                              airtelPhoneNumber,
                                              newCountryCode,
                                            );
                                          }
                                        }}
                                        labelId="airtel-phone-country-code-label"
                                        id="airtel-phone-country-code"
                                        label="Code"
                                      >
                                        {Object.keys(PhoneCountryCodeMap).map(
                                          (key) => {
                                            const countryCode =
                                              key as COUNTRY_CODE;
                                            return (
                                              <MenuItem
                                                key={key}
                                                value={countryCode}
                                              >
                                                {`${PhoneCountryCodeMap[countryCode].flag} ${PhoneCountryCodeMap[countryCode].code}`}
                                              </MenuItem>
                                            );
                                          },
                                        )}
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      label="Phone Number"
                                      value={airtelPhoneNumber}
                                      onChange={(e) => {
                                        setAirtelPhoneNumber(e.target.value);
                                        if (airtelPhoneError) {
                                          validateAirtelPhone();
                                        }
                                      }}
                                      onBlur={validateAirtelPhone}
                                      fullWidth
                                      placeholder="782484464"
                                      error={!!airtelPhoneError}
                                      helperText={airtelPhoneError}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Pay With Card Content */}
                              {method.value === PaymentMethod.CREDIT_DEBIT && (
                                <Box>
                                  {/* Email Field */}
                                  <TextField
                                    label="Email"
                                    type="email"
                                    value={cardEmail}
                                    onChange={(e) => {
                                      setCardEmail(e.target.value);
                                      if (cardEmailError) {
                                        setCardEmailError("");
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!cardEmail) {
                                        setCardEmailError("Email is required");
                                      } else if (
                                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                          cardEmail,
                                        )
                                      ) {
                                        setCardEmailError(
                                          "Invalid email format",
                                        );
                                      } else {
                                        setCardEmailError("");
                                      }
                                    }}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    placeholder="Email"
                                    error={!!cardEmailError}
                                    helperText={cardEmailError}
                                  />

                                  {/* Card Information */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ mb: 1, fontWeight: 500 }}
                                    >
                                      Card information
                                    </Typography>
                                    <Box
                                      sx={{ display: "flex", gap: 1, mb: 1 }}
                                    >
                                      <TextField
                                        placeholder="1234 1234 1234 1234"
                                        value={cardNumber}
                                        onChange={(e) => {
                                          const formatted = formatCardNumber(
                                            e.target.value,
                                          );
                                          setCardNumber(formatted);
                                          if (cardNumberError) {
                                            setCardNumberError("");
                                          }
                                        }}
                                        onBlur={() => {
                                          if (!cardNumber) {
                                            setCardNumberError(
                                              "Card number is required",
                                            );
                                          } else if (
                                            !validateCardNumber(cardNumber)
                                          ) {
                                            setCardNumberError(
                                              "Invalid card number",
                                            );
                                          } else {
                                            setCardNumberError("");
                                          }
                                        }}
                                        fullWidth
                                        sx={{ flex: 2 }}
                                        error={!!cardNumberError}
                                        helperText={cardNumberError}
                                        inputProps={{ maxLength: 19 }}
                                      />
                                      <TextField
                                        placeholder="MM / YY"
                                        value={cardExpiry}
                                        onChange={(e) => {
                                          const formatted = formatExpiry(
                                            e.target.value,
                                          );
                                          setCardExpiry(formatted);
                                          if (cardExpiryError) {
                                            setCardExpiryError("");
                                          }
                                        }}
                                        onBlur={() => {
                                          if (!cardExpiry) {
                                            setCardExpiryError(
                                              "Expiry date is required",
                                            );
                                          } else if (
                                            !validateExpiry(cardExpiry)
                                          ) {
                                            setCardExpiryError(
                                              "Invalid or expired date (MM/YY)",
                                            );
                                          } else {
                                            setCardExpiryError("");
                                          }
                                        }}
                                        sx={{ flex: 1 }}
                                        error={!!cardExpiryError}
                                        helperText={cardExpiryError}
                                        inputProps={{ maxLength: 5 }}
                                      />
                                      <TextField
                                        placeholder="CVC"
                                        value={cardCVC}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(
                                            /\D/g,
                                            "",
                                          );
                                          setCardCVC(value);
                                          if (cardCVCError) {
                                            setCardCVCError("");
                                          }
                                        }}
                                        onBlur={() => {
                                          if (!cardCVC) {
                                            setCardCVCError("CVC is required");
                                          } else if (!validateCVC(cardCVC)) {
                                            setCardCVCError(
                                              "Invalid CVC (3-4 digits)",
                                            );
                                          } else {
                                            setCardCVCError("");
                                          }
                                        }}
                                        sx={{ flex: 1 }}
                                        error={!!cardCVCError}
                                        helperText={cardCVCError}
                                        inputProps={{ maxLength: 4 }}
                                      />
                                    </Box>
                                    <Box
                                      sx={{ display: "flex", gap: 1, mt: 1 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        VISA • Mastercard • Amex • Discover
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Name on Card */}
                                  <TextField
                                    label="Name on card"
                                    value={cardName}
                                    onChange={(e) => {
                                      setCardName(e.target.value);
                                      if (cardNameError) {
                                        setCardNameError("");
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!cardName) {
                                        setCardNameError(
                                          "Name on card is required",
                                        );
                                      } else if (cardName.trim().length < 2) {
                                        setCardNameError(
                                          "Name must be at least 2 characters",
                                        );
                                      } else {
                                        setCardNameError("");
                                      }
                                    }}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    placeholder="Name on card"
                                    error={!!cardNameError}
                                    helperText={cardNameError}
                                  />

                                  {/* Country or Region */}
                                  <TextField
                                    label="Country or region"
                                    select
                                    value={cardCountry}
                                    onChange={(e) =>
                                      setCardCountry(e.target.value)
                                    }
                                    fullWidth
                                    SelectProps={{
                                      native: true,
                                    }}
                                    sx={{ mb: 2 }}
                                  >
                                    <option value="RW">Rwanda</option>
                                    <option value="UG">Uganda</option>
                                    <option value="KE">Kenya</option>
                                    <option value="TZ">Tanzania</option>
                                  </TextField>
                                </Box>
                              )}

                              {/* PayPal Content */}
                              {method.value === PaymentMethod.PAYPAL && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    You will be redirected to PayPal to complete
                                    your payment.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 4 }} />

                {subscribeError && (
                  <Alert
                    severity="error"
                    onClose={() => setSubscribeError("")}
                    sx={{ mb: 2 }}
                  >
                    {subscribeError}
                  </Alert>
                )}

                {/* Subscribe Button */}
                <Button
                  variant="contained"
                  onClick={handleSubscribe}
                  disabled={createSubscriptionMutation.isPending}
                  fullWidth
                  sx={{
                    backgroundColor: "#936DAE",
                    color: "white",
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                    mb: 2,
                    "&:hover": {
                      backgroundColor: "#A682C0",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(147, 109, 174, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {createSubscriptionMutation.isPending
                    ? "Subscribing…"
                    : "Subscribe"}
                </Button>

                {/* Disclaimer */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: "center", lineHeight: 1.6 }}
                >
                  By confirming your subscription, you allow Membership to
                  charge your card for this payment and future payments in
                  accordance with their terms.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </AnimatedContainer>
      </Container>
    </Box>
  );
};

export default OrderSummary;
